import React, { useState, useMemo } from "react";
import { ChevronRight, ChevronDown, AlertOctagon, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useInsights } from "../../data/insightsStore";

const riskBand = (n) =>
  n >= 60
    ? { bg: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500", label: "Critical" }
    : n >= 30
    ? { bg: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", label: "Watch" }
    : { bg: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500", label: "Healthy" };

const probColor = (p) => (p >= 80 ? "text-indigo-700" : p >= 60 ? "text-slate-900" : p >= 40 ? "text-amber-700" : "text-rose-700");

const Bar = ({ value, color }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
    </div>
    <span className="text-[10.5px] font-mono-cf text-slate-700 w-7 text-right">{value}%</span>
  </div>
);

const barColor = (v) => (v >= 80 ? "bg-indigo-500" : v >= 50 ? "bg-amber-500" : "bg-rose-500");
const agentColor = "bg-indigo-100 text-indigo-700";

export const RiskTable = () => {
  const navigate = useNavigate();
  const { data } = useInsights();
  const riskDeals = useMemo(() => data?.risk_deals || [], [data?.risk_deals]);

  const [openRow, setOpenRow] = useState(null);
  const [filter, setFilter] = useState("all");

  const counts = useMemo(() => ({
    all: riskDeals.length,
    high: riskDeals.filter((d) => d.risk >= 60).length,
    watch: riskDeals.filter((d) => d.risk >= 30 && d.risk < 60).length,
    healthy: riskDeals.filter((d) => d.risk < 30).length,
  }), [riskDeals]);

  const filtered = useMemo(() => {
    if (filter === "high") return riskDeals.filter((d) => d.risk >= 60);
    if (filter === "watch") return riskDeals.filter((d) => d.risk >= 30 && d.risk < 60);
    if (filter === "healthy") return riskDeals.filter((d) => d.risk < 30);
    return riskDeals;
  }, [filter, riskDeals]);

  const tabs = [
    { id: "all", label: "All transactions", count: counts.all },
    { id: "high", label: "Critical", count: counts.high },
    { id: "watch", label: "Watch", count: counts.watch },
    { id: "healthy", label: "Healthy", count: counts.healthy },
  ];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white cf-shadow overflow-hidden" data-testid="risk-table">
      <div className="px-5 py-4 border-b border-slate-200/80 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">AI monitor</p>
          <h2 className="font-display text-[18px] font-bold text-slate-900">Live transaction risk</h2>
        </div>
        <div className="flex items-center gap-1 bg-white rounded-lg border border-slate-200 p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`risk-tab-${t.id}`}
              onClick={() => setFilter(t.id)}
              className={`h-8 px-3 rounded-md text-[12px] font-medium transition-all ${
                filter === t.id ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label}
              <span className={`ml-1.5 text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${filter === t.id ? "bg-white/15" : "bg-slate-100 text-slate-500"}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="px-5 py-10 text-center text-[13px] text-slate-500" data-testid="risk-table-empty">
          No deals in this band yet. Use the demo seed (POST /api/admin/seed-demo) to populate the workspace.
        </div>
      ) : (
        <div className="overflow-x-auto cf-scroll">
          <table className="w-full text-left min-w-[1200px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold border-b border-slate-100">
                <th className="px-5 py-3 font-semibold w-[24%]">Property</th>
                <th className="px-3 py-3 font-semibold">Value</th>
                <th className="px-3 py-3 font-semibold">Agent</th>
                <th className="px-3 py-3 font-semibold">Chain</th>
                <th className="px-3 py-3 font-semibold">Risk</th>
                <th className="px-3 py-3 font-semibold">Mortgage</th>
                <th className="px-3 py-3 font-semibold">Solicitor</th>
                <th className="px-3 py-3 font-semibold">Enquiries</th>
                <th className="px-3 py-3 font-semibold">Docs</th>
                <th className="px-3 py-3 font-semibold">Predicted</th>
                <th className="px-3 py-3 font-semibold">Prob.</th>
                <th className="px-3 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const r = riskBand(d.risk);
                const isOpen = openRow === d.id;
                return (
                  <React.Fragment key={d.id}>
                    <tr
                      onClick={() => setOpenRow(isOpen ? null : d.id)}
                      data-testid={`risk-row-${d.id}`}
                      className={`border-b border-slate-50 last:border-b-0 hover:bg-slate-50/60 cursor-pointer transition-colors ${d.risk >= 60 ? "bg-rose-50/20" : ""}`}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg overflow-hidden ring-1 ring-slate-200 shrink-0 bg-slate-100">
                            {d.image ? (
                              <img src={d.image} alt={d.address} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-indigo-200 to-indigo-100" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold text-slate-900 truncate">{d.address}</p>
                            <p className="text-[11px] text-slate-500 truncate">{d.postcode || d.stage_label}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3.5"><p className="font-display text-[14px] font-bold text-slate-900">{d.value}</p></td>
                      <td className="px-3 py-3.5">
                        <span className={`w-7 h-7 inline-flex items-center justify-center rounded-full text-[10.5px] font-semibold ${agentColor}`}>{d.agent?.initials || "—"}</span>
                      </td>
                      <td className="px-3 py-3.5"><p className={`font-display text-[14px] font-bold ${d.chain >= 85 ? "text-indigo-700" : d.chain >= 70 ? "text-slate-900" : "text-amber-700"}`}>{d.chain}</p></td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${r.bg}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${r.dot} ${d.risk >= 60 ? "cf-pulse" : ""}`} />
                            {r.label} · {d.risk}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3.5 min-w-[120px]"><Bar value={d.mortgage} color={barColor(d.mortgage)} /></td>
                      <td className="px-3 py-3.5 min-w-[120px]"><Bar value={d.solicitor} color={barColor(d.solicitor)} /></td>
                      <td className="px-3 py-3.5 min-w-[120px]"><Bar value={d.enquiries} color={barColor(d.enquiries)} /></td>
                      <td className="px-3 py-3.5 min-w-[120px]"><Bar value={d.docs} color={barColor(d.docs)} /></td>
                      <td className="px-3 py-3.5">
                        <p className="text-[12px] text-slate-900 font-medium whitespace-nowrap">{d.predicted}</p>
                        {d.flag && (
                          <p className="text-[10.5px] text-rose-600 inline-flex items-center gap-1 mt-0.5"><AlertOctagon size={10} />{d.flag}</p>
                        )}
                      </td>
                      <td className="px-3 py-3.5"><p className={`font-display text-[16px] font-bold ${probColor(d.probability)}`}>{d.probability}%</p></td>
                      <td className="px-3 py-3.5 text-right">{isOpen ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}</td>
                    </tr>

                    {isOpen && (
                      <tr className="bg-slate-50/60" data-testid={`risk-row-${d.id}-detail`}>
                        <td colSpan={12} className="px-5 py-4">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="rounded-xl bg-white border border-slate-200 p-4">
                              <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Risk decomposition</p>
                              <ul className="space-y-2.5">
                                {[
                                  { label: "Solicitor responsiveness", v: d.solicitor },
                                  { label: "Mortgage progress",        v: d.mortgage },
                                  { label: "Enquiries velocity",       v: d.enquiries },
                                  { label: "Document completeness",    v: d.docs },
                                  { label: "Chain stability",          v: d.chain },
                                ].map((row) => (
                                  <li key={row.label}>
                                    <div className="flex items-center justify-between text-[11.5px] mb-1">
                                      <span className="text-slate-700">{row.label}</span>
                                      <span className="font-mono-cf text-slate-900 font-medium">{row.v}%</span>
                                    </div>
                                    <Bar value={row.v} color={barColor(row.v)} />
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-4">
                              <p className="text-[10.5px] uppercase tracking-[0.18em] text-indigo-300 font-semibold">AI commentary</p>
                              <p className="font-display text-[14px] font-bold mt-2 leading-snug">
                                Probability of successful exchange: {d.probability}%
                              </p>
                              <p className="text-[12px] text-slate-300 mt-2">
                                Resolving the top blocker (<b className="text-white">{d.flag || "no critical blocker"}</b>) is modelled to lift exchange probability by {Math.min(98, d.probability + Math.round((100 - d.probability) * 0.6))}% within 14 days.
                              </p>
                              <button
                                onClick={() => navigate(`/deal-rooms/${d.id}`)}
                                data-testid={`risk-row-${d.id}-open-deal`}
                                className="mt-3 h-8 px-3 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white text-[11.5px] font-semibold inline-flex items-center gap-1.5"
                              >
                                Open Deal Room <ExternalLink size={11} />
                              </button>
                            </div>

                            <div className="rounded-xl bg-white border border-slate-200 p-4">
                              <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Recommended actions</p>
                              <div className="space-y-2">
                                {d.solicitor < 50 && <ActionRow label="Auto-escalate solicitor" tone="rose" />}
                                {d.docs < 60     && <ActionRow label="Issue document chase pack" tone="amber" />}
                                {d.mortgage < 50 && <ActionRow label="Reschedule mortgage step" tone="amber" />}
                                <ActionRow label="Open Deal Room timeline" tone="slate" onClick={() => navigate(`/deal-rooms/${d.id}`)} />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const tones = {
  rose: "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100",
  amber: "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100",
  slate: "bg-white border-slate-200 text-slate-700 hover:border-slate-300",
};

const ActionRow = ({ label, tone, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full inline-flex items-center justify-between h-9 px-3 rounded-lg border text-[12px] font-semibold transition-colors ${tones[tone]}`}
  >
    {label}
    <ChevronRight size={12} />
  </button>
);
